import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  MessageSquare,
  Send,
  Reply,
  Trash2,
  Edit,
  X,
  Loader2,
  CornerDownRight,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import type { DocumentComment } from '../../types';

interface CommentSectionProps {
  documentId: string;
  currentUserId: string;
  currentUserName: string;
  currentUserRole: 'administrator' | 'student' | 'faculty';
  focusCommentId?: string;
}

export function CommentSection({
  documentId,
  currentUserId,
  currentUserName,
  currentUserRole,
  focusCommentId,
}: CommentSectionProps) {
  const { accessToken } = useAuth();
  const [comments, setComments] = useState<DocumentComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  const fetchComments = async () => {
    if (!accessToken) return;
    try {
      const res = await api.getComments(documentId, accessToken);
      setComments(res.comments || []);
    } catch {
      // Silently fail on load - comments are supplementary
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [documentId, accessToken]);

  // Focus reply input when replying
  useEffect(() => {
    if (replyingTo && replyInputRef.current) {
      replyInputRef.current.focus();
    }
  }, [replyingTo]);

  useEffect(() => {
    if (!focusCommentId || loading || !comments.some((comment) => comment.id === focusCommentId)) {
      return;
    }

    setHighlightedCommentId(focusCommentId);

    const scrollTimer = window.setTimeout(() => {
      document.getElementById(`document-comment-${documentId}-${focusCommentId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 150);

    const clearTimer = window.setTimeout(() => {
      setHighlightedCommentId((current) => (current === focusCommentId ? null : current));
    }, 3500);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(clearTimer);
    };
  }, [focusCommentId, comments, loading, documentId]);

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !accessToken) return;
    setSubmitting(true);
    try {
      await api.createComment(documentId, { content: newComment.trim() }, accessToken);
      setNewComment('');
      await fetchComments();
      toast.success('Comment posted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentId: string) => {
    if (!replyContent.trim() || !accessToken) return;
    setSubmitting(true);
    try {
      await api.createComment(documentId, { content: replyContent.trim(), parentId }, accessToken);
      setReplyContent('');
      setReplyingTo(null);
      await fetchComments();
      toast.success('Reply posted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateComment = async (commentId: string) => {
    if (!editContent.trim() || !accessToken) return;
    setSubmitting(true);
    try {
      await api.updateComment(documentId, commentId, { content: editContent.trim() }, accessToken);
      setEditingId(null);
      setEditContent('');
      await fetchComments();
      toast.success('Comment updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!accessToken) return;
    try {
      await api.deleteComment(documentId, commentId, accessToken);
      await fetchComments();
      toast.success('Comment deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete comment');
    }
  };

  const startEditing = (comment: DocumentComment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
    setReplyingTo(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'faculty':
        return 'Instructor';
      case 'student':
        return 'Student';
      case 'administrator':
        return 'Admin';
      default:
        return role;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Separate top-level comments and replies
  const topLevelComments = comments.filter((c) => !c.parentId);
  const getReplies = (parentId: string) =>
    comments.filter((c) => c.parentId === parentId);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'faculty':
        return { bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-200 dark:border-blue-800', avatar: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-blue-200 dark:border-blue-700' };
      case 'administrator':
        return { bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-200 dark:border-red-800', avatar: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300', badge: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 border-red-200 dark:border-red-700' };
      default:
        return { bg: 'bg-green-50 dark:bg-green-950/30', border: 'border-green-200 dark:border-green-800', avatar: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', badge: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-200 dark:border-green-700' };
    }
  };

  const renderComment = (comment: DocumentComment, isReply = false) => {
    const replies = getReplies(comment.id);
    const isOwn = comment.userId === currentUserId;
    const canDelete = isOwn || currentUserRole === 'administrator';
    const isEditing = editingId === comment.id;
    const roleColor = getRoleColor(comment.userRole);
    const isHighlighted = highlightedCommentId === comment.id;

    return (
      <div id={`document-comment-${documentId}-${comment.id}`} key={comment.id} className={`${isReply ? 'ml-4 sm:ml-8 mt-2' : 'mt-3'}`}>
        <div className={`rounded-lg border p-3 sm:p-4 transition-all duration-500 ${roleColor.bg} ${roleColor.border} ${isOwn ? 'ring-1 ring-primary/20' : ''} ${isHighlighted ? 'ring-2 ring-amber-400 shadow-lg shadow-amber-200/70' : ''}`}>
          <div className="flex gap-2.5 sm:gap-3">
            <Avatar className={`${isReply ? 'h-7 w-7' : 'h-9 w-9'} shrink-0`}>
              <AvatarFallback className={`text-xs font-semibold ${roleColor.avatar}`}>
                {getInitials(comment.userName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <span className={`${isReply ? 'text-sm' : 'text-sm sm:text-base'} font-semibold truncate max-w-[140px] sm:max-w-none`}>{comment.userName}</span>
                <Badge variant="outline" className={`text-[11px] px-1.5 py-0 h-[18px] font-medium border ${roleColor.badge}`}>
                  {getRoleLabel(comment.userRole)}
                </Badge>
                <span className="text-[11px] sm:text-xs text-muted-foreground ml-auto sm:ml-0">
                  {formatDate(comment.createdAt)}
                </span>
                {comment.updatedAt !== comment.createdAt && (
                  <span className="text-[11px] sm:text-xs text-muted-foreground italic">(edited)</span>
                )}
              </div>

              {isEditing ? (
                <div className="mt-2 space-y-2">
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="min-h-[60px] text-sm bg-background"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleUpdateComment(comment.id)}
                      disabled={submitting || !editContent.trim()}
                    >
                      {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelEditing}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p className={`${isReply ? 'text-sm' : 'text-sm sm:text-[15px]'} mt-1.5 whitespace-pre-wrap break-words leading-relaxed`}>{comment.content}</p>
              )}

            {!isEditing && (
              <div className="flex gap-0.5 sm:gap-1 mt-2 -ml-1.5">
                {!isReply && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-background/60"
                    onClick={() => {
                      setReplyingTo(replyingTo === comment.id ? null : comment.id);
                      setReplyContent('');
                      setEditingId(null);
                    }}
                  >
                    <Reply className="h-3.5 w-3.5 mr-1" />
                    Reply{replies.length > 0 ? ` (${replies.length})` : ''}
                  </Button>
                )}
                {isOwn && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-background/60"
                    onClick={() => startEditing(comment)}
                  >
                    <Edit className="h-3.5 w-3.5 mr-1" />
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs font-medium text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={() => handleDeleteComment(comment.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Delete
                  </Button>
                )}
              </div>
            )}

            {/* Reply input for this comment */}
            {replyingTo === comment.id && (
              <div className="mt-3 flex gap-2 items-start">
                <CornerDownRight className="h-4 w-4 text-muted-foreground mt-2.5 shrink-0" />
                <div className="flex-1 space-y-2">
                  <Textarea
                    ref={replyInputRef}
                    placeholder={`Reply to ${comment.userName}...`}
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    className="min-h-[60px] text-sm bg-background"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        handleSubmitReply(comment.id);
                      }
                    }}
                  />
                  <div className="flex flex-wrap gap-2 items-center">
                    <Button
                      size="sm"
                      onClick={() => handleSubmitReply(comment.id)}
                      disabled={submitting || !replyContent.trim()}
                    >
                      {submitting ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <Send className="h-3 w-3 mr-1" />
                      )}
                      Reply
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setReplyingTo(null);
                        setReplyContent('');
                      }}
                    >
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                    <span className="text-[11px] text-muted-foreground hidden sm:inline ml-auto">Ctrl+Enter to submit</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Render replies outside the comment card */}
        {replies.length > 0 && (
          <div className="ml-4 sm:ml-8 mt-1 border-l-2 border-primary/20 pl-1">
            {replies.map((reply) => renderComment(reply, true))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading comments...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-primary" />
        <h4 className="text-base font-semibold">
          Comments
        </h4>
        {comments.length > 0 && (
          <Badge variant="secondary" className="text-xs font-medium">
            {comments.length}
          </Badge>
        )}
      </div>

      {/* New comment input */}
      <div className="space-y-2 rounded-lg border bg-muted/30 p-3 sm:p-4">
        <Textarea
          ref={commentInputRef}
          placeholder="Write a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="min-h-[70px] text-sm bg-background resize-y"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              handleSubmitComment();
            }
          }}
        />
        <div className="flex justify-between items-center flex-wrap gap-2">
          <span className="text-xs text-muted-foreground hidden sm:inline">
            Ctrl+Enter to submit
          </span>
          <Button
            size="sm"
            onClick={handleSubmitComment}
            disabled={submitting || !newComment.trim()}
            className="ml-auto"
          >
            {submitting ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Send className="h-3 w-3 mr-1" />
            )}
            Post Comment
          </Button>
        </div>
      </div>

      {/* Comments list */}
      {topLevelComments.length === 0 ? (
        <div className="text-center py-6 space-y-1">
          <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No comments yet. Be the first to comment!
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {topLevelComments.map((comment) => renderComment(comment))}
        </div>
      )}
    </div>
  );
}
